import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ApiError,
  deleteTriageAttachment,
  errorMessage,
  fetchTriageDocuments,
  saveTriageAdvisory,
  uploadTriageAttachment,
} from '../../lib/api';
import type { TriageAdvisory, TriageDocuments } from '../../lib/types';

export interface PendingAttachment {
  id: string;
  file: File;
  status: 'queued' | 'uploading' | 'failed';
  error?: string;
}

export function useTriageDocuments(eventId: number, previousReason = '') {
  const [documents, setDocuments] = useState<TriageDocuments | null>(null);
  const [content, setContent] = useState('');
  const [templateName, setTemplateName] = useState<string | null>(null);
  const [baseline, setBaseline] = useState<{ content: string; templateName: string | null }>({
    content: '',
    templateName: null,
  });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [queue, setQueue] = useState<PendingAttachment[]>([]);
  const mutationLock = useRef(false);
  const generation = useRef(0);
  const queueSequence = useRef(0);

  const load = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError(null);
    setError(null);
    try {
      const result = await fetchTriageDocuments(eventId);
      if (request !== generation.current) return;
      setDocuments(result);
      // Preserve assessments written before the unified editor existed. This
      // is an initial value, not an unsaved user edit or a persisted advisory.
      const initialContent =
        result.advisory.revision === 0 ? previousReason : result.advisory.content;
      setContent(initialContent);
      setTemplateName(result.advisory.template_name);
      setBaseline({ content: initialContent, templateName: result.advisory.template_name });
      setConflict(false);
    } catch (err) {
      if (request === generation.current) setLoadError(errorMessage(err));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [eventId, previousReason]);

  useEffect(() => {
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  const dirty =
    documents !== null && (content !== baseline.content || templateName !== baseline.templateName);
  const needsSave =
    documents !== null &&
    (content !== documents.advisory.content || templateName !== documents.advisory.template_name);

  async function save(): Promise<void> {
    if (!documents) throw new Error('Dokumente sind noch nicht geladen.');
    if (mutationLock.current)
      throw new Error('Bitte warten Sie, bis die Dateien gespeichert sind.');
    if (!needsSave) return;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    try {
      const advisory: TriageAdvisory = await saveTriageAdvisory(eventId, {
        content,
        template_name: templateName,
        revision: documents.advisory.revision,
      });
      setDocuments((current) => current && { ...current, advisory });
      setBaseline({ content: advisory.content, templateName: advisory.template_name });
    } catch (err) {
      setError(errorMessage(err));
      setConflict(err instanceof ApiError && err.status === 409);
      throw err;
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }

  async function upload(files: File[], retryId?: string): Promise<void> {
    if (!documents || mutationLock.current || files.length === 0) return;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    const items: PendingAttachment[] = files.map((file) => ({
      id: retryId ?? `upload-${++queueSequence.current}`,
      file,
      status: 'queued',
    }));
    setQueue((current) => [...current.filter((item) => item.id !== retryId), ...items]);
    let savedCount = documents.attachments.length;
    try {
      for (const item of items) {
        setQueue((current) =>
          current.map((row) => (row.id === item.id ? { ...row, status: 'uploading' } : row)),
        );
        try {
          const extension = `.${item.file.name.split('.').pop()?.toLowerCase()}`;
          if (!documents.limits.allowed_extensions.includes(extension)) {
            throw new Error('Dieses Dateiformat wird nicht unterstützt.');
          }
          if (item.file.size === 0) throw new Error('Die Datei ist leer.');
          if (item.file.size > documents.limits.max_file_size)
            throw new Error('Die Datei überschreitet die maximale Größe.');
          if (savedCount >= documents.limits.max_files)
            throw new Error('Die maximale Anzahl an Anhängen ist erreicht.');
          const attachment = await uploadTriageAttachment(eventId, item.file);
          savedCount += 1;
          setDocuments(
            (current) =>
              current && { ...current, attachments: [...current.attachments, attachment] },
          );
          setQueue((current) => current.filter((row) => row.id !== item.id));
        } catch (err) {
          setQueue((current) =>
            current.map((row) =>
              row.id === item.id ? { ...row, status: 'failed', error: errorMessage(err) } : row,
            ),
          );
        }
      }
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }

  async function remove(attachmentId: string): Promise<void> {
    if (mutationLock.current) return;
    mutationLock.current = true;
    setBusy(true);
    setError(null);
    try {
      await deleteTriageAttachment(eventId, attachmentId);
      setDocuments(
        (current) =>
          current && {
            ...current,
            attachments: current.attachments.filter((item) => item.id !== attachmentId),
          },
      );
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      mutationLock.current = false;
      setBusy(false);
    }
  }

  return {
    documents,
    content,
    templateName,
    setContent,
    setTemplateName,
    loading,
    loadError,
    error,
    busy: busy || preparing,
    setPreparing,
    conflict,
    dirty,
    needsSave,
    queue,
    load,
    save,
    upload,
    remove,
    dismissQueued: (id: string) => setQueue((current) => current.filter((item) => item.id !== id)),
  };
}

export type TriageDocumentState = ReturnType<typeof useTriageDocuments>;
